import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { TableSkeleton } from '../../../../components/TableSkeleton';
import CustomTextField from '../../../../components/Textfield';
import useEmployees from '../../../../hooks/useEmployees';
import { truncateFileName } from '../../../../utils/truncateFileName';

const EmployeeManagement = () => {
  const [deleteEmployeeId, setDeleteEmployeeId] = useState(null);
  const [viewCertificate, setViewCertificate] = useState(null);
  const [open, setOpen] = useState(false);

  const { employees, isEmployeesLoading, addEmployee, isAddingEmployee, deleteEmployee, isDeletingEmployee } = useEmployees();

  const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm();

  const handleOpenDialog = () => {
    reset({ name: '', employeeId: '', certificate_no: '', certificate: null });
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  const handleRemoveCertificate = () => {
    setValue('certificate', null);
  };

  const handleSaveEmployee = async (values) => {
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('employeeId', values.employeeId);
      formData.append('certificate_no', values.certificate_no);

      if (values.certificate && values.certificate[0]) {
        formData.append('certificate', values.certificate[0]);
      }

      await addEmployee(formData);
      toast.success('Employee added successfully');
      handleCloseDialog();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong');
    }
  };

  const handleDeleteEmployee = async () => {
    try {
      await deleteEmployee(deleteEmployeeId);
      toast.success('Employee deleted successfully');
      setDeleteEmployeeId(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Something went wrong');
    }
  };

  if (isEmployeesLoading) {
    return <TableSkeleton />;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Employees List
        </Typography>
        <Button
          variant="contained"
          sx={{ background: '#454751d1', fontSize: 15 }}
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add Employee
        </Button>
      </Box>

      {/* Employees Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { fontSize: '14px', fontWeight: 'bold' } }}>
              <TableCell>Employee Name</TableCell>
              <TableCell>Employee ID</TableCell>
              <TableCell>Certificate No</TableCell>
              <TableCell>Certificate</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees?.length > 0 ? (
              employees.map((employee) => (
                <TableRow key={employee._id} sx={{ '& td': { fontSize: '14px' } }}>
                  <TableCell>{employee.name}</TableCell>
                  <TableCell>{employee.employeeId}</TableCell>
                  <TableCell>{employee.certificate_no}</TableCell>
                  <TableCell>
                    {employee.certificate?.url ? (
                      <Chip
                        label={<Typography fontSize={13}>{truncateFileName(employee.certificate.name || 'Certificate')}</Typography>}
                        size="small"
                        color="primary"
                        variant="outlined"
                        icon={<InsertDriveFileIcon fontSize="small" />}
                        onClick={() => setViewCertificate(employee.certificate.url)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">No certificate</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="success"
                      onClick={() => setViewCertificate(employee.certificate?.url)}
                      size="small"
                      disabled={!employee.certificate?.url}
                    >
                      <InsertDriveFileIcon fontSize="large" />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => setDeleteEmployeeId(employee._id)}
                      size="small"
                    >
                      <DeleteIcon fontSize="large" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="h6">No employees found. Add an employee to get started.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add Employee Dialog */}
      <Dialog open={open} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <form onSubmit={handleSubmit(handleSaveEmployee)}>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              Add New Employee
              <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: 500 }}>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12 }}>
                <CustomTextField
                  {...register('name', { required: 'Employee Name is required' })}
                  fullWidth
                  label="Employee Name*"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <CustomTextField
                  {...register('employeeId', { required: 'Employee ID is required' })}
                  fullWidth
                  label="Employee ID*"
                  error={!!errors.employeeId}
                  helperText={errors.employeeId?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <CustomTextField
                  {...register('certificate_no', { required: 'Certificate No is required' })}
                  fullWidth
                  label="Certificate No*"
                  error={!!errors.certificate_no}
                  helperText={errors.certificate_no?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                {!watch('certificate')?.[0]?.name && (
                  <input
                    type="file"
                    id="certificate"
                    accept="application/pdf,image/*"
                    {...register('certificate', {
                      required: 'Certificate file is required',
                      validate: (v) => (v && v.length > 0) || 'Please upload a certificate file',
                    })}
                    style={{ display: 'none' }}
                  />
                )}
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <Button
                    variant="contained"
                    component="span"
                    onClick={() => document.getElementById('certificate')?.click()}
                    disabled={!!watch('certificate')?.[0]?.name}
                    sx={{ fontSize: 15, background: '#454751d1' }}
                  >
                    Choose Certificate File
                  </Button>
                  {watch('certificate')?.[0]?.name && (
                    <Chip
                      label={
                        <Typography fontSize={14}>
                          {truncateFileName(watch('certificate')?.[0]?.name)}
                        </Typography>
                      }
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={handleRemoveCertificate}
                    />
                  )}
                </Box>
                {errors.certificate && (
                  <Typography variant="caption" color="error" ml={1.5} fontSize={12}>
                    {errors.certificate.message}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ fontSize: 14, color: '#454751d1' }}>
              Cancel
            </Button>
            <Button
              sx={{ fontSize: 14, background: '#454751d1' }}
              type="submit"
              variant="contained"
              disabled={isAddingEmployee}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Employee Dialog */}
      <Dialog open={!!deleteEmployeeId} onClose={() => setDeleteEmployeeId(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Delete Employee
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setDeleteEmployeeId(null)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 3 }}>
          Are you sure you want to delete this employee?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEmployeeId(null)} sx={{ fontSize: 13, color: '#454751d1' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteEmployee}
            sx={{ fontSize: 13 }}
            variant="contained"
            disabled={isDeletingEmployee}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* View Certificate Dialog */}
      <Dialog open={!!viewCertificate} onClose={() => setViewCertificate(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            View Certificate
            <IconButton edge="end" color="inherit" onClick={() => setViewCertificate(null)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewCertificate ? (
            <iframe
              src={viewCertificate}
              title="Employee Certificate"
              width="100%"
              height="600px"
              style={{ border: '1px solid #ccc', borderRadius: '4px' }}
            />
          ) : (
            <Typography>No certificate available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewCertificate(null)} sx={{ fontSize: 14, color: '#454751d1' }}>
            Close
          </Button>
          <Button
            sx={{ fontSize: 14, background: '#454751d1' }}
            variant="contained"
            onClick={() => {
              const link = document.createElement('a');
              link.href = viewCertificate;
              link.download = 'employee-certificate';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeManagement;
